import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CalendarCheck, Clock, Mail, Phone, Users, Building2, MessageSquare } from 'lucide-react';
import { AdminEmptyState, AdminLoadingState, AdminPageHeader, AdminSurface } from '@/components/admin/AdminPrimitives';

const STATUSES = ['New', 'Reviewed', 'Approved', 'Rejected'];

const STATUS_STYLES = {
  New: 'bg-blue-50 text-blue-700 border-blue-100',
  Reviewed: 'bg-amber-50 text-amber-700 border-amber-100',
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  Rejected: 'bg-red-50 text-red-700 border-red-100',
};

export default function AdminActivityRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [savingId, setSavingId] = useState('');

  const fetchRegistrations = async () => {
    try {
      const res = await api.get('/admin/activity-registrations');
      setRegistrations(res.data);
      setSelectedRegistration((current) => {
        if (!current) return res.data[0] || null;
        return res.data.find((item) => item.id === current.id) || res.data[0] || null;
      });
    } catch {
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRegistrations(); }, []);

  const counts = useMemo(() => {
    return STATUSES.reduce((acc, status) => {
      acc[status] = registrations.filter((item) => item.status === status).length;
      return acc;
    }, {});
  }, [registrations]);

  const updateStatus = async (registration, status) => {
    setSavingId(registration.id);
    try {
      const res = await api.put(`/admin/activity-registrations/${registration.id}/status`, { status });
      const updated = res.data;
      setRegistrations((current) => current.map((item) => item.id === updated.id ? updated : item));
      setSelectedRegistration(updated);
      toast.success('Registration status updated');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update status');
    } finally {
      setSavingId('');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <AdminLoadingState label="Loading registrations" />;
  }

  return (
    <div data-testid="admin-activity-registrations-page">
      <AdminPageHeader
        eyebrow="Activities"
        title="Activity Registrations"
        description="Private registration submissions from the Activities page."
      >
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((status) => (
            <span key={status} className={`rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_STYLES[status]}`}>
              {status}: {counts[status] || 0}
            </span>
          ))}
        </div>
      </AdminPageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-2">
          {registrations.length === 0 ? (
            <AdminEmptyState icon={CalendarCheck} title="No registrations yet" description="Activity registration submissions will appear here." />
          ) : (
            registrations.map((registration) => (
              <button
                key={registration.id}
                data-testid={`registration-item-${registration.id}`}
                onClick={() => setSelectedRegistration(registration)}
                className={`mscm-admin-list-item w-full rounded-[8px] border p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D5BE3]/30 ${
                  selectedRegistration?.id === registration.id
                    ? 'bg-[#EBF2FA] border-[#2D5BE3]/30'
                    : 'bg-white border-[#E2E8F0] hover:bg-[#F8FAFC]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#0F172A]">{registration.full_name}</p>
                    <p className="mt-1 line-clamp-2 text-xs text-[#526274]">{registration.activity_name}</p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[registration.status] || STATUS_STYLES.New}`}>
                    {registration.status || 'New'}
                  </span>
                </div>
                <p className="mt-3 flex items-center gap-1 text-xs text-[#94A3B8]">
                  <Clock size={11} /> {formatDate(registration.submitted_at)}
                </p>
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-2">
          {selectedRegistration ? (
            <AdminSurface className="p-6" data-testid="registration-detail">
              <div className="flex flex-col gap-4 border-b border-[#E2E8F0] pb-5 md:flex-row md:items-start md:justify-between">
                <div>
                  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_STYLES[selectedRegistration.status] || STATUS_STYLES.New}`}>
                    {selectedRegistration.status || 'New'}
                  </span>
                  <h2 className="mt-3 text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {selectedRegistration.activity_name}
                  </h2>
                  <p className="mt-1 text-sm text-[#526274]">{selectedRegistration.activity_date}</p>
                </div>
                <select
                  value={selectedRegistration.status || 'New'}
                  onChange={(event) => updateStatus(selectedRegistration, event.target.value)}
                  disabled={savingId === selectedRegistration.id}
                  data-testid="registration-status-select"
                  className="h-10 rounded-[8px] border border-[#DDEBFA] bg-white px-3 text-sm font-medium text-[#0F172A] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2D5BE3]/20"
                >
                  {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <Detail icon={Users} label="Applicant" value={selectedRegistration.full_name} />
                <Detail icon={Mail} label="Email" value={selectedRegistration.email} href={`mailto:${selectedRegistration.email}`} />
                <Detail icon={Phone} label="Phone" value={selectedRegistration.phone} href={`tel:${selectedRegistration.phone}`} />
                <Detail icon={Building2} label="Church / organization" value={selectedRegistration.church_organization} />
                <Detail icon={Users} label="Participants" value={selectedRegistration.participants_count} />
                <Detail icon={Clock} label="Submitted" value={formatDate(selectedRegistration.submitted_at)} />
              </div>

              {(selectedRegistration.participant_group_name || selectedRegistration.city_country || selectedRegistration.category || selectedRegistration.contact_person) ? (
                <div className="mt-6 rounded-[8px] border border-[#E2E8F0]/70 bg-[#F8FBFF] p-4">
                  <h3 className="text-sm font-semibold text-[#0F172A]">Konkou details</h3>
                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <SmallDetail label="Participant/group" value={selectedRegistration.participant_group_name} />
                    <SmallDetail label="City/country" value={selectedRegistration.city_country} />
                    <SmallDetail label="Category" value={selectedRegistration.category} />
                    <SmallDetail label="Contact person" value={selectedRegistration.contact_person} />
                  </div>
                </div>
              ) : null}

              <div className="mt-6">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
                  <MessageSquare size={16} className="text-[#2D5BE3]" />
                  Message / notes
                </div>
                <div className="mt-3 rounded-[8px] border border-[#E2E8F0]/70 bg-[#F8FBFF] p-4">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#526274]">
                    {selectedRegistration.message || 'No notes provided.'}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <Button asChild className="rounded-full bg-[#2D5BE3] text-white hover:bg-[#1E4FA1]">
                  <a href={`mailto:${selectedRegistration.email}?subject=Re: ${encodeURIComponent(selectedRegistration.activity_name)} registration`}>
                    Reply via Email
                  </a>
                </Button>
              </div>
            </AdminSurface>
          ) : (
            <AdminEmptyState icon={CalendarCheck} title="Select a registration" description="Choose a submission from the list to review contact details and status." />
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value, href }) {
  const content = href ? (
    <a href={href} className="font-semibold text-[#2D5BE3] hover:underline">{value || '-'}</a>
  ) : (
    <p className="font-semibold text-[#0F172A]">{value || '-'}</p>
  );

  return (
    <div className="flex items-start gap-3 rounded-[8px] border border-[#E2E8F0]/80 bg-white/70 p-4 shadow-[0_8px_24px_rgba(11,31,77,0.04)]">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-[#EBF2FA]">
        <Icon size={18} className="text-[#2D5BE3]" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-[0.12em] text-[#94A3B8]">{label}</p>
        {content}
      </div>
    </div>
  );
}

function SmallDetail({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.12em] text-[#94A3B8]">{label}</p>
      <p className="text-sm font-semibold text-[#0F172A]">{value}</p>
    </div>
  );
}
